import { useAxiosApi } from '@/hooks/useAxiosApi'
import useSWR from 'swr'
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import RouterLink from '@/components/ui/RouterLink'
import { Badge, Loader, Table } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'

const TYPE_LABELS = {
    shopify: { label: 'Shopify', color: 'green' },
    retailer: { label: 'Revendeur', color: 'blue' },
    manual: { label: 'Manuel', color: 'gray' },
}

function DirectionSection({ direction }) {
    const { source, target, moveable } = direction

    if (moveable.length === 0) {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    <h2 className="text-base font-semibold text-gray-900">
                        {source.name}
                    </h2>
                    <IconArrowRight size={16} className="text-gray-400" />
                    <h2 className="text-base font-semibold text-gray-900">
                        {target.name}
                    </h2>
                </div>
                <div className="text-sm text-gray-500 italic">
                    Aucune BP déplaçable vers {target.name}
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">
                    {source.name}
                </h2>
                <IconArrowRight size={16} className="text-gray-400" />
                <h2 className="text-base font-semibold text-gray-900">
                    {target.name}
                </h2>
                <Badge color="green" variant="light" size="sm">
                    {moveable.length} déplaçable{moveable.length > 1 ? 's' : ''}
                </Badge>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>BP</Table.Th>
                            <Table.Th>Type</Table.Th>
                            <Table.Th>Composants</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {moveable.map((fo) => {
                            const typeInfo = TYPE_LABELS[fo.type] || TYPE_LABELS.manual
                            return (
                                <Table.Tr key={fo.id}>
                                    <Table.Td>
                                        <RouterLink
                                            href={`/fulfillment_orders/${fo.id}`}
                                            className="text-blue-600 hover:underline font-medium"
                                        >
                                            {fo.display_name}
                                        </RouterLink>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge
                                            color={typeInfo.color}
                                            variant="light"
                                            size="xs"
                                        >
                                            {typeInfo.label}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td className="text-sm text-gray-600">
                                        {fo.components?.map((c, i) => (
                                            <span key={i}>
                                                {i > 0 && ', '}
                                                {c.qty > 1 ? `${c.qty}× ` : ''}{c.name}
                                            </span>
                                        ))}
                                    </Table.Td>
                                </Table.Tr>
                            )
                        })}
                    </Table.Tbody>
                </Table>
            </div>
        </div>
    )
}

export default function ReallocationSuggestions() {
    const { requestGet } = useAxiosApi()

    const { data, isLoading } = useSWR(
        '/fulfillment-orders/reallocation-suggestions',
        requestGet,
        { revalidateOnFocus: true }
    )

    const directions = data?.data?.directions ?? []

    return (
        <ScrollableScreen
            breadcrumbs={[
                { label: 'Dashboard', href: '/' },
                { label: 'Préparations', href: '/fulfillment_orders' },
                { label: 'Déplaçables' },
            ]}
        >
            <main className="@container w-full">
                <div className="p-5 space-y-8">
                    {isLoading && (
                        <div className="flex items-center justify-center py-20">
                            <Loader size="sm" />
                        </div>
                    )}

                    {!isLoading && directions.length === 0 && (
                        <div className="text-sm text-gray-500 text-center py-10">
                            Aucune donnée disponible.
                        </div>
                    )}

                    {directions.map((direction, i) => (
                        <DirectionSection key={i} direction={direction} />
                    ))}
                </div>
            </main>
        </ScrollableScreen>
    )
}
